/**
 * A `pcs` count read back in packets — PIECE GUARD beyond Stock In.
 *
 * Stock In asks once per `pcs` item how many pieces come in one packet and keeps the
 * answer in ItemPieceProfiles (`pieces_per_pack`, item-level, editable in Settings ›
 * Piece Guard). Wastage and the closing-stock count are kept in those same pieces, so
 * somebody who throws out (or counts) 2 packets of a 4-piece item and types 2 has
 * recorded 2 pieces, not 8. Stock In catches that slip on the way in; the wastage
 * screen and the closing-stock sheet catch it by saying the number back as packets
 * before it is saved: "8 pieces = 2 packets".
 *
 * Read-only. Nothing here changes the quantity that is sent — the person either
 * confirms it as it stands or goes back and changes it themselves.
 */

// The spellings the inventory overview uses for a piece-counted item. A profile only
// exists for an item stocked in as `pcs`, so a wider list cannot invent a popup; it
// only stops one being missed because the label was spelt "pieces".
const PIECE_UNITS = ['pcs', 'pc', 'piece', 'pieces'];

const round3 = (n) => Math.round(n * 1000) / 1000;
const count = (n, one, many) => `${round3(n)} ${n === 1 ? one : many}`;

/** Is this wastage-screen raw item counted in pieces? */
export const isPieceCountedItem = (item) => {
  if (!item) return false;
  return [item.unitSymbol, item.unit]
    .some(u => PIECE_UNITS.includes(String(u || '').trim().toLowerCase()));
};

/**
 * Same filter the submit applies: a `raw_<id>` row with a positive quantity. Anything
 * the submit would drop must not be read back either.
 */
const pieceRows = (wastedItems, rawItems) => {
  const rows = [];
  for (const [key, qty] of Object.entries(wastedItems || {})) {
    if (!String(key).startsWith('raw_')) continue;
    const pieces = parseFloat(qty);
    if (!(pieces > 0)) continue;
    const item = (rawItems || []).find(r => r.id === key);
    const itemId = Number(item?.originalId);
    if (!isPieceCountedItem(item) || !Number.isInteger(itemId) || itemId <= 0) continue;
    rows.push({ key, item, itemId, pieces });
  }
  return rows;
};

/** The inventory item ids whose Stock In answer the read-back needs. */
export const pieceWastageItemIds = (wastedItems, rawItems) =>
  [...new Set(pieceRows(wastedItems, rawItems).map(r => r.itemId))];

/** Whole packets, and the pieces left over. */
export const splitIntoPackets = (pieces, piecesPerPack) => {
  // The epsilon keeps 12 pieces of a 4-pack at 3 packets when 12 arrived as 11.9999999.
  const packets = Math.floor((pieces + 1e-9) / piecesPerPack);
  const loose = round3(pieces - packets * piecesPerPack);
  return { packets, loose: loose > 0 ? loose : 0 };
};

/** "8 pieces = 2 packets", "10 pieces = 2 packets + 2 pieces", "3 pieces = less than 1 packet". */
export const packetsSentence = ({ pieces, packets, loose }) => {
  const left = count(pieces, 'piece', 'pieces');
  if (packets === 0) return `${left} = less than 1 packet`;
  const right = count(packets, 'packet', 'packets');
  return loose > 0 ? `${left} = ${right} + ${count(loose, 'piece', 'pieces')}` : `${left} = ${right}`;
};

/**
 * The read-back for one count of pieces, or null when there is nothing to say back:
 * nothing counted, no Stock In answer yet, or a single-piece item (coconut, cake).
 *
 * @param {{id, itemId, name, pieces}} count
 * @param {object|null} profile  one entry of GET /stock-in/v2/piece-profiles
 * @returns {{id, itemId, name, pieces, piecesPerPack, packets, loose}|null}
 */
export const piecePackLine = ({ id, itemId, name, pieces }, profile) => {
  const piecesPerPack = Number(profile?.piecesPerPack);
  if (!(pieces > 0) || !Number.isInteger(piecesPerPack) || piecesPerPack < 2) return null;
  const { packets, loose } = splitIntoPackets(pieces, piecesPerPack);
  return { id, itemId, name, pieces, piecesPerPack, packets, loose };
};

/**
 * Wastage: one line per piece-counted ingredient whose Stock In answer is a packet.
 *
 * @param {object} wastedItems  the form's { 'raw_12': 8, ... }
 * @param {Array}  rawItems     the screen's raw items (utils/wastageItemMapping)
 * @param {object} profiles     { [itemId]: profile | null } from GET /stock-in/v2/piece-profiles
 * @returns {Array} empty when there is nothing to read back — and then the screen
 *   behaves exactly as it did before.
 */
export const piecePackLines = (wastedItems, rawItems, profiles) =>
  pieceRows(wastedItems, rawItems)
    .map(({ key, item, itemId, pieces }) =>
      piecePackLine({ id: key, itemId, name: item.name, pieces }, profiles?.[itemId]))
    .filter(Boolean);
